import { InputHTMLAttributes } from 'react'

import { UserItems } from '~/components/Modals/GemShop/GemShop.types'
import { InAppKeyboardLayoutId } from '~/in-app-keyboard/types/inAppKeyboard.types'
import { PetState } from '~/redux/slices/pet-state'
import { QuestionExtras, Submission } from '~/aioracle/types/learning-context.types'

import { AppLanguage } from '~/types/locale'

export enum BossBehaviors {
  INTRO = 'ready',
  RIGHT = 'right',
  WRONG = 'wrong',
  VICTORY = 'victory',
  DEFEAT = 'defeat',
  RESTART = 'restart',
  RELOAD = 'reload',
  MUTE = 'mute',
  UNMUTE = 'unmute',
}

export enum LineTypes {
  LINE = 'line',
  LINE_SEGMENT = 'line_segment',
  RAY = 'ray',
}

export interface BossMessage {
  action?: BossBehaviors
  type?: string
  language?: AppLanguage
  petState?: PetState
  userItems?: UserItems
  name?: string
  reward?: {
    subject: string
    currentBootcamp: string
    kudos: number
    hasBadge: boolean
    defeatedBosses: string[] // bootcamp number of bosses that are already defeated
  }
}

export interface CircleCombinationsMessage {
  type?: string
  questionMessageData?: CircleCombinationsQuestionMessageData
  language?: AppLanguage
}

export interface MatchingPairsMessage {
  type?: string
  questionMessageData?: MatchingPairsQuestionMessageData
  language?: AppLanguage
}

export interface CircleCombinationsQuestionMessageData {
  circleElements?: string[]
  randomizeCircleElementsOrder?: boolean
  validCircleCombinations?: string[]
  combinationsToFind?: number
}

export interface InteractiveShapeViewerMessage {
  type?: string
  shape?: string
}

export interface MatchingPairsQuestionMessageData {
  pairs: string[]
}

export interface ActivityActionButtons {
  formValid: boolean
  correctAnswer: boolean
  submitAnswerAction: () => void
  goToNextQuestionAction?: () => void
  clearFormAction: () => void
}

export interface ActivityProps {
  activityType: number
  answerState: boolean | null
  difficultyLevel: number
  levelId: string
  onNextQuestionClick: () => void
  /** `submission` rides only the graded call, and the progress/practice-activity path ignores it. */
  onAnswerStateChange: (
    value: boolean | null,
    questionId?: string,
    bigActivity?: boolean,
    submission?: Submission,
  ) => void
  /** Fires before any Check, where `onAnswerStateChange` only reports after grading. A separate prop
   *  because that one's non-graded form fires on every edit and feeds the analytics path. */
  onCurrentQuestionChange?: (questionId: string, extras?: QuestionExtras) => void
  /** Interactive widgets only: the on-screen task, plus for figure-reading widgets the value the student
   *  must read off it (the backend renders it only inside a never-write guard). Emit through `useEmitInstance`. */
  onInstanceChange?: (task: string | null, expectedAnswer?: string, facts?: string[]) => void
  /** NumberLineMulti only: the rendered figure's asset URL. Emit through `useEmitInstanceImage`. */
  onInstanceImageChange?: (imageUrl: string) => void
  actionButtonsFC?: React.FC<ActivityActionButtons>
  autofocus?: boolean
}

export interface ActivityGroupProps extends ActivityProps {
  activityName: string
  correctConsecutiveAnswersCount?: number
  keyFromIntro?: string
  moduleKeyFromIntro?: string
}

export interface InputsAnswer {
  [key: string]: string
}

export interface Slice {
  id: number
  isColored: boolean
  point1?: {
    x: number
    y: number
  }
  point2?: {
    x: number
    y: number
  }
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  id?: string
  isError?: boolean
  isSmall?: boolean
  inputSize?: 'sm' | 'md' | 'xs'
  isWide?: boolean
  maxDigits?: number
  name?: string
  onChange: (value: string, name?: string) => void
  type?: 'number' | 'text'
  value: string
  disabled?: boolean
  autoFocus?: boolean
  className?: string
  onEnter?: () => void
  keyboardLayoutId?: InAppKeyboardLayoutId | null
}

export interface BlockContent {
  _type: string
  text: string
  body: string
  srcKey: string
  alt: string
  children: BlockContent[]
}

export interface BlockContentWrapper {
  element: BlockContent[]
}

export enum activityTypes {
  multiplication = 'multiplication',
  pemdas = 'pemdas',
  factorization = 'factorization',
  fraction = 'fraction',
  planarFigures = 'planar-figures',
  algebraicExpressions = 'algebraic-expressions',
  numbers = 'numbers',
  statistics = 'statistics',
}
